import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { SessionService } from '../../shared/session.service';
import { TokenExchangeService } from '../../shared/token-exchange.service';
import { AccountService } from '../account.service';
import { ActivatedRoute, Router } from '@angular/router';
import { take, switchMap, tap, finalize } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { Account } from '../../shared/models';
import { OnboardingService } from '../../shared/onboarding.service';
import { onboardingConfig } from './onboarding-config';
import { OnboardingSteps } from './onboarding-steps';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BusinessProfileComponent } from './business-profile/business-profile.component';
import { WebsiteAdminAccessComponent } from './website-admin-access/website-admin-access.component';
import { CampaignSurveyComponent } from './campaign-survey/campaign-survey.component';
import { CampaignSurvey } from '../../shared/models/campaign-survey';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrl: './onboarding.component.scss'
})
export class OnboardingComponent implements OnInit {
  loading = false;
  campaignSurvey: CampaignSurvey;
  steps = onboardingConfig.map(step => ({ ...step }));
  readonly OnboardingSteps = OnboardingSteps;
  account: Account;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private onboardingService: OnboardingService,
    private accountService: AccountService,
    private sessionService: SessionService,
    private tokenExchangeService: TokenExchangeService,
    private modalService: NgbModal,
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.loading = true;

    this.authenticate().pipe(
      switchMap((account) => forkJoin([
        this.onboardingService.businessProfileExists(account.url).pipe(take(1)),
        this.onboardingService.credentialsExists(account.id).pipe(take(1)),
        this.onboardingService.getCampaignSurvey(account.id).pipe(take(1))
      ])),
      finalize(() => {
        this.resetRoute();
        this.loading = false;
        this.cdRef.detectChanges();
      })
    ).subscribe(([hasBusinessProfile, hasCredentials, campaignSurvey]) => {
      this.onboardingStep(OnboardingSteps.BusinessProfile).complete = hasBusinessProfile;
      this.onboardingStep(OnboardingSteps.WebsiteAccess).complete = hasCredentials;
      this.onboardingStep(OnboardingSteps.ContentSurvey).complete = this.isCampaignSurveyComplete(campaignSurvey);
      this.campaignSurvey = campaignSurvey;
    }, (err) => {
      this.router.navigateByUrl('log-out');
    });
  }

  openModal(id: OnboardingSteps) {
    let inputs: Record<string, any> = { account: this.account };
    let modalConfig: Record<string, any> = { size: 'lg' };

    let modalComponent;
    switch (id) {
      case OnboardingSteps.BusinessProfile:
        window.open(this.translateService.instant(this.onboardingStep(OnboardingSteps.BusinessProfile).actionLinkKey), '_blank');
        modalComponent = BusinessProfileComponent;
        break;
      case OnboardingSteps.WebsiteAccess:
        modalComponent = WebsiteAdminAccessComponent;
        break;
      case OnboardingSteps.ContentSurvey:
        modalComponent = CampaignSurveyComponent;
        inputs.survey = this.campaignSurvey;
        modalConfig.size = 'xl';
        modalConfig.backdrop = 'static';
        modalConfig.scrollable = true;
        break;
      default:
        throw new Error('Invalid onboarding step');
    }

    const modalRef = this.modalService.open(modalComponent, modalConfig);
    Object.assign(modalRef.componentInstance, inputs);

    modalRef.result.then(
      (_) => this.recheckStep(id),
      (_) => { /* no action needed on dismiss */ }
    );
  }

  private recheckStep(id: OnboardingSteps) {
    switch (id) {
      case OnboardingSteps.BusinessProfile:
        this.onboardingService.businessProfileExists(this.account.url).pipe(take(1)).subscribe(result => {
          this.onboardingStep(OnboardingSteps.BusinessProfile).complete = result;
          this.handleOnboardingCompletionIfNeeded();
        });
        break;
      case OnboardingSteps.WebsiteAccess:
        this.onboardingService.credentialsExists(this.account.id).pipe(take(1)).subscribe(result => {
          this.onboardingStep(OnboardingSteps.WebsiteAccess).complete = result;
          this.handleOnboardingCompletionIfNeeded();
        });
        break;
      case OnboardingSteps.ContentSurvey:
        this.onboardingService.isCampaignSurveyComplete(this.account.id).pipe(take(1)).subscribe(result => {
          this.onboardingStep(OnboardingSteps.ContentSurvey).complete = result;
          this.handleOnboardingCompletionIfNeeded();
        });
        break;
    }
  }

  private handleOnboardingCompletionIfNeeded() {
    if (this.steps.every(s => s.complete)) {
      this.router.navigateByUrl('accounts');
    }
  }

  private isCampaignSurveyComplete(survey: CampaignSurvey) {
    return !!survey &&
      !!survey.domain &&
      !!survey.vertical &&
      !!survey.businessOverview &&
      !!survey.location &&
      !!survey.product;
  }

  private onboardingStep(step: OnboardingSteps) {
    return this.steps.find(x => x.id === step);
  }

  private authenticate(): Observable<Account> {
    const yToken = this.route.snapshot.queryParams['y'];

    return this.sessionService.hasSession.pipe(
      take(1),
      switchMap(hasSession => {
        if (hasSession) {
          return this.sessionService.account.pipe(take(1));
        }
        return this.tokenExchangeService.exchange().pipe(
          switchMap(() => this.accountService.default(yToken)),
          switchMap(accountId => this.accountService.account(accountId)),
          tap(account => {
            this.sessionService.setAccount(account);
          })
        );
      }),
      tap(account => this.account = account)
    );
  }

  private resetRoute() {
    const { isLandingPage } = this.route.snapshot.queryParams;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: isLandingPage !== undefined
        ? { isLandingPage }
        : {},
      replaceUrl: true
    });
  }
}
